import { ApiClient } from './base/apiClient';
import type { ApiResponse } from '~/types/api/common/apiResponse';
import type { ChangePasswordRequest } from '~/types/api/account/changePassword';
import type { GetMeResponse } from '~/types/api/account/getMe';
import type { ApplyChangeTruckDriverAccountRequest, ApplyChangeTruckDriverAccountResponse } from '~/types/api/account/applyChangeTruckDriverAccount';
import type { ApplyChangeOfficeAccountRequest, ApplyChangeOfficeAccountResponse } from '~/types/api/account/applyChangeOfficeAccount';
import type { ApplyChangeJrfAccountRequest, ApplyChangeJrfAccountResponse } from '~/types/api/account/applyChangeJrfAccount';
import type { ApplyChangePartnerAccountRequest, ApplyChangePartnerAccountResponse } from '~/types/api/account/applyChangePartnerAccount';
import type {
  ApplyChangeGuestTruckDriverAccountRequest,
  ApplyChangeGuestTruckDriverAccountResponse,
} from '~/types/api/account/applyChangeGuestTruckDriverAccount';
import type { ApplyChangeCorporationAccountRequest, ApplyChangeCorporationAccountResponse } from '~/types/api/account/applyChangeCorporationAccount';
import type { GetApplicationStatusResponse } from '~/types/api/account/getApplicationStatus';
import type { GetApplicationDetailResponse } from '~/types/api/adminApplications/getApplicationDetail';

const BASE_PATH = '/account';

/**
 * アカウント申請に関するリポジトリ
 */
export class AccountRepository {
  private apiClient: ApiClient;

  constructor(displayId: string) {
    this.apiClient = new ApiClient(displayId);
  }

  /**
   * [sp-api-006] パスワード変更API
   *
   * @param request リクエスト
   */
  async changePassword(request: ChangePasswordRequest): Promise<ApiResponse<void>> {
    return await this.apiClient.put(BASE_PATH + '/password', request);
  }

  /**
   * [sp-api-608] マイアカウント情報取得API
   */
  async getMe(): Promise<ApiResponse<GetMeResponse>> {
    return await this.apiClient.get<GetMeResponse>(BASE_PATH + '/me');
  }

  /**
   * [sp-api-609] アカウント変更申請（ドライバー）API
   */
  async applyChangeTruckDriverAccount(
    request: ApplyChangeTruckDriverAccountRequest,
  ): Promise<ApiResponse<ApplyChangeTruckDriverAccountResponse>> {
    return await this.apiClient.post<ApplyChangeTruckDriverAccountResponse>(BASE_PATH + '/edit-apply/driver', request);
  }

  /**
   * [sp-api-610] アカウント変更申請（事務員）API
   */
  async applyChangeOfficeAccount(
    request: ApplyChangeOfficeAccountRequest,
  ): Promise<ApiResponse<ApplyChangeOfficeAccountResponse>> {
    return await this.apiClient.post<ApplyChangeOfficeAccountResponse>(BASE_PATH + '/edit-apply/office', request);
  }

  /**
   * [sp-api-611] アカウント変更申請（JRF社員）API
   */
  async applyChangeJrfAccount(
    request: ApplyChangeJrfAccountRequest,
  ): Promise<ApiResponse<ApplyChangeJrfAccountResponse>> {
    return await this.apiClient.post<ApplyChangeJrfAccountResponse>(BASE_PATH + '/edit-apply/jrf', request);
  }

  /**
   * [sp-api-612] アカウント変更申請（協力会社）API
   */
  async applyChangePartnerAccount(
    request: ApplyChangePartnerAccountRequest,
  ): Promise<ApiResponse<ApplyChangePartnerAccountResponse>> {
    return await this.apiClient.post<ApplyChangePartnerAccountResponse>(BASE_PATH + '/edit-apply/partner', request);
  }

  /**
   * [sp-api-613] アカウント変更申請（臨時ドライバー）API
   */
  async applyChangeGuestTruckDriverAccount(
    request: ApplyChangeGuestTruckDriverAccountRequest,
  ): Promise<ApiResponse<ApplyChangeGuestTruckDriverAccountResponse>> {
    return await this.apiClient.post<ApplyChangeGuestTruckDriverAccountResponse>(BASE_PATH + '/edit-apply/guest', request);
  }

  /**
   * [sp-api-614] アカウント変更申請（法人）API
   */
  async applyChangeCorporationAccount(
    request: ApplyChangeCorporationAccountRequest,
  ): Promise<ApiResponse<ApplyChangeCorporationAccountResponse>> {
    return await this.apiClient.post<ApplyChangeCorporationAccountResponse>(BASE_PATH + '/edit-apply/corporation', request);
  }

  /**
   * [sp-api-615] 申請状況確認API
   */
  async getApplicationStatus(): Promise<ApiResponse<GetApplicationStatusResponse>> {
    return await this.apiClient.get<GetApplicationStatusResponse>(BASE_PATH + '/apply/status');
  }

  /**
   * [sp-api-616] アカウント申請取消API
   */
  async cancelChangeApplication(): Promise<ApiResponse<void>> {
    return await this.apiClient.put(BASE_PATH + '/cancel-apply');
  }

  /**
   * [sp-api-617] アカウント変更申請詳細API
   */
  async getMyApplicationDetail(): Promise<ApiResponse<GetApplicationDetailResponse>> {
    return await this.apiClient.get<GetApplicationDetailResponse>(BASE_PATH + '/applications/me');
  }
}
